import { type ChangeEvent, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { Typography } from '@/components/ui/typography';
import { PrefixedInput } from '@/components/ui/prefixed-input';
import { TermsInfo } from './TermsInfo';
import { useDropdownOptions } from '../../properties/services/useDropdownOptions';
import {
  formatPhoneNumberInField,
  hasNoUrlUserInfo,
  stripUrlProtocol,
  toHttpsUrl
} from '../../../util/formatting';
import { getFieldError } from '../utils/fieldVisibility';
import type { AdditionalDetailsFormValues } from '../../../types/registration.types';

interface AdditionalDetailsFormProps {
  initialValues: AdditionalDetailsFormValues;
  isLoading: boolean;
  onSubmit: (
    values: AdditionalDetailsFormValues,
    setFieldError: (field: string, message: string) => void
  ) => void;
  onSignOut: () => void;
}

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('Please enter first name')
    .matches(
      /^[a-zA-Z.,' -]*$/,
      'Please enter letters, spaces, hyphen, period, comma and apostrophe only'
    ),
  lastName: yup
    .string()
    .required('Please enter last name')
    .matches(
      /^[a-zA-Z.,' -]*$/,
      'Please enter letters, spaces, hyphen, period, comma and apostrophe only'
    ),
  phone: yup
    .string()
    .required('Please enter your phone number')
    .test(
      'is-valid-phone',
      'Please enter a valid 10-digit phone number',
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return (
          (digits.length === 10 && !digits.startsWith('1')) ||
          (digits.length === 11 && digits.startsWith('1'))
        );
      }
    ),
  properties: yup
    .string()
    .required('Please select an option from the above list.'),
  companyName: yup.string().required('Please enter company name'),
  website: yup
    .string()
    .transform((value) => toHttpsUrl(value))
    .required('Please enter website')
    .url('Please enter a valid website URL')
    .test('no-userinfo', 'Please enter a valid website URL', hasNoUrlUserInfo)
});

const AdditionalDetailsForm = ({
  initialValues,
  isLoading,
  onSubmit,
  onSignOut
}: AdditionalDetailsFormProps) => {
  const propertiesRef = useRef<{ clearValue: () => void }>(null);
  const propertiesOwnedOptions = useDropdownOptions('propertyOwned', 'entity');

  return (
    <Formik<AdditionalDetailsFormValues>
      validationSchema={schema}
      initialValues={initialValues}
      validateOnBlur
      validateOnChange={false}
      onSubmit={(values, { setFieldError }) => onSubmit(values, setFieldError)}
      enableReinitialize
    >
      {({
        handleSubmit,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        submitCount
      }) => {
        const firstNameError = getFieldError(
          touched.firstName,
          values.firstName,
          errors.firstName,
          submitCount
        );
        const lastNameError = getFieldError(
          touched.lastName,
          values.lastName,
          errors.lastName,
          submitCount
        );
        const companyNameError = getFieldError(
          touched.companyName,
          values.companyName,
          errors.companyName,
          submitCount
        );
        const websiteError = getFieldError(
          touched.website,
          values.website,
          errors.website,
          submitCount
        );
        const phoneError = getFieldError(
          touched.phone,
          values.phone,
          errors.phone,
          submitCount
        );
        const propertiesError = getFieldError(
          touched.properties,
          values.properties,
          errors.properties,
          submitCount
        );

        return (
          <Form
            id="additional-details-form"
            data-testid="additional-details-form"
            noValidate
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            {/* Header */}
            <div className="text-center -mt-2">
              <Typography as="p" variant="body" className="text-[#212529]">
                Almost there, tell us a little more about yourself
              </Typography>
            </div>

            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first-name" required>
                  First Name
                </Label>
                <Input
                  id="first-name"
                  name="firstName"
                  placeholder="Enter First Name"
                  maxLength={35}
                  value={values.firstName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFieldValue(
                      'firstName',
                      e.target.value,
                      touched.firstName
                    )
                  }
                  onBlur={handleBlur}
                  className={firstNameError ? 'border-destructive' : ''}
                  data-testid="first-name_id"
                />
                <FormError message={firstNameError} />
              </div>
              <div>
                <Label htmlFor="last-name" required>
                  Last Name
                </Label>
                <Input
                  id="last-name"
                  name="lastName"
                  placeholder="Enter Last Name"
                  maxLength={35}
                  value={values.lastName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('lastName', e.target.value, touched.lastName)
                  }
                  onBlur={handleBlur}
                  className={lastNameError ? 'border-destructive' : ''}
                  data-testid="last-name_id"
                />
                <FormError message={lastNameError} />
              </div>
            </div>

            {/* Company name */}
            <div>
              <Label htmlFor="company-name" required>
                Company Name
              </Label>
              <Input
                id="company-name"
                name="companyName"
                placeholder="Enter Company Name"
                maxLength={160}
                value={values.companyName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue(
                    'companyName',
                    e.target.value,
                    touched.companyName
                  )
                }
                onBlur={handleBlur}
                className={companyNameError ? 'border-destructive' : ''}
                data-testid="company-name_id"
              />
              <FormError message={companyNameError} />
            </div>

            {/* Website */}
            <div>
              <Label htmlFor="website" required>
                Website
              </Label>
              <PrefixedInput
                id="website"
                name="website"
                prefix="https://"
                placeholder="example.com"
                maxLength={255}
                value={values.website}
                onChange={(e) =>
                  setFieldValue(
                    'website',
                    stripUrlProtocol(e.target.value),
                    touched.website
                  )
                }
                onBlur={handleBlur}
                className={websiteError ? 'border-destructive' : ''}
                data-testid="website_id"
              />
              <FormError message={websiteError} />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phoneNumber" required>
                Phone
              </Label>
              <Input
                id="phoneNumber"
                name="phone"
                placeholder="Enter Phone Number"
                value={values.phone}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue(
                    'phone',
                    formatPhoneNumberInField(e.target.value),
                    touched.phone
                  )
                }
                onBlur={handleBlur}
                className={phoneError ? 'border-destructive' : ''}
                data-testid="phoneNumber_id"
              />
              <FormError message={phoneError} />
            </div>

            {/* Properties owned */}
            <div>
              <SimpleSelect
                options={propertiesOwnedOptions}
                name="properties"
                md="12"
                controlId="properties"
                ariaDescribedBy="properties-select"
                placeholder="Select"
                ref={propertiesRef}
                label="How Many Units Do You Manage?"
                errors={propertiesError}
                setFieldValue={setFieldValue}
                additionalData={touched.properties}
                isRequired
                field="properties"
              />
            </div>

            {/* Terms */}
            <div>
              <TermsInfo />
            </div>

            {/* Submit */}
            <div>
              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading && <Loader2 className="animate-spin" />}
                Submit Registration
              </Button>
            </div>

            {/* Sign out */}
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                // Standalone link-styled action, so it carries the 44px touch
                // floor itself -- see button.tsx's link-variant comment.
                className="max-lg:min-h-11"
                onClick={onSignOut}
              >
                Sign Out
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AdditionalDetailsForm;
